import { useEffect, useState } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { Plus, Save } from "lucide-react";
import { getHeaders } from "../../api/utils";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import ChartCard from "./chartcard";
import { useTranslation } from "react-i18next";

const ResponsiveGridLayout = WidthProvider(Responsive);

const DASHBOARD_KEY = "dashboard_settings"; // ajusta si quieres por ruta
const DEFAULT_BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const DEFAULT_COLS = { lg: 12, md: 10, sm: 8, xs: 6, xxs: 4 };
const STORAGE_KEY = "dashboard_layouts_v1"; // fallback local (opcional)

const CARD_W = 4;
const CARD_H = 8;

export default function Dashboard() {
    const [counter, setCounter] = useState(0); // se recalculará al cargar
    const [items, setItems] = useState([]);
    const [currentBreakpoint, setCurrentBreakpoint] = useState("lg");
    const [hydrated, setHydrated] = useState(false);
    const { t } = useTranslation();

    // const lineData = React.useMemo(() => {
    //     if (!Array.isArray(historicoTotalIndice) || historicoTotalIndice.length < 2)
    //         return [];
    //     return historicoTotalIndice.slice(1).map(([name, total]) => ({
    //         name: String(name),
    //         value: Number(total) || 0,
    //     }));
    // }, [historicoTotalIndice]);

    // Area usa el mismo histórico pero con llaves {t, v}
    // const areaData = React.useMemo(() => {
    //     if (!Array.isArray(historicoTotalIndice) || historicoTotalIndice.length < 2)
    //         return [];
    //     return historicoTotalIndice.slice(1).map(([name, total]) => ({
    //         t: String(name),
    //         v: Number(total) || 0,
    //     }));
    // }, [historicoTotalIndice]);

    const baseLayout = () => {
        return [];
    }

    const [layouts, setLayouts] = useState({
        lg: baseLayout(),
        md: baseLayout(),
        sm: baseLayout(),
        xs: baseLayout(),
        xxs: baseLayout(),
    });

    // Debounce para guardar en backend sin spamear
    // const saveTimerRef = useRef(null);
    const scheduleSave = async () => {
        // if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
        // saveTimerRef.current = setTimeout(async () => {
        try {
            const payload = {
                bp: currentBreakpoint,
                layout: (layouts[currentBreakpoint] || []).map(
                    ({ i, x, y, w, h }) => ({ i, x, y, w, h })
                ),
                items: items.map(({ i, type, title }) => ({ i, type, title })),
            };
            const res = await fetch(
                `https://gestiondeflota.boltrack.net/apirastreogestion/api/dashboard-layouts/${encodeURIComponent(
                    DASHBOARD_KEY
                )}`,
                {
                    method: "PUT",
                    headers: getHeaders(),
                    body: JSON.stringify(payload),
                }
            );
            if (!res.ok) localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
        } catch {
            localStorage.setItem(
                STORAGE_KEY,
                JSON.stringify({
                    bp: currentBreakpoint,
                    layout: layouts[currentBreakpoint] || [],
                    items: items.map(({ i, type, title }) => ({ i, type, title })),
                })
            );
        }
        // }, 500);
    };

    // Utilidad para calcular el contador según IDs existentes
    const calcCounterFromItems = (list) => {
        const n = Math.max(
            0,
            ...list.map((m) => parseInt(m.i, 10)).filter((x) => !Number.isNaN(x))
        );
        return n;
    }

    // CARGA inicial: primero backend; si 404/err, no crea nada (dashboard vacío)
    const cargarLayouts = async () => {
        try {
            const res = await fetch(
                `https://gestiondeflota.boltrack.net/apirastreogestion/api/dashboard-layouts/${encodeURIComponent(
                    DASHBOARD_KEY
                )}`,
                { headers: getHeaders() }
            );
            if (res.ok) {
                let { layouts: serverLayouts, items: serverItems } = await res.json();

                // Si el backend envía JSON como string, parseamos
                if (typeof serverLayouts === "string") {
                    try {
                        serverLayouts = JSON.parse(serverLayouts);
                    } catch { }
                }
                if (typeof serverItems === "string") {
                    try {
                        serverItems = JSON.parse(serverItems);
                    } catch { }
                }

                // Layouts seguros
                const emptyLayouts = { lg: [], md: [], sm: [], xs: [], xxs: [] };
                const safeLayouts =
                    serverLayouts && typeof serverLayouts === "object"
                        ? serverLayouts
                        : emptyLayouts;
                setLayouts(safeLayouts);

                // Elegimos el layout no vacío más razonable (bp actual, si no lg, si no md, etc.)
                const pick =
                    safeLayouts[currentBreakpoint] &&
                        safeLayouts[currentBreakpoint].length
                        ? safeLayouts[currentBreakpoint]
                        : safeLayouts.lg && safeLayouts.lg.length
                            ? safeLayouts.lg
                            : safeLayouts.md && safeLayouts.md.length
                                ? safeLayouts.md
                                : safeLayouts.sm && safeLayouts.sm.length
                                    ? safeLayouts.sm
                                    : safeLayouts.xs && safeLayouts.xs.length
                                        ? safeLayouts.xs
                                        : safeLayouts.xxs || [];

                const byId = new Map(pick.map((l) => [l.i, l]));
                const restoredItems = Array.isArray(serverItems)
                    ? serverItems.map((m) => {
                        const pos = byId.get(m.i);
                        return {
                            i: m.i,
                            type: m.type,
                            title: m.title,
                            x: pos?.x ?? 0,
                            y: pos?.y ?? Infinity,
                            w: pos?.w ?? CARD_W,
                            h: pos?.h ?? CARD_H,
                        };
                    })
                    : [];

                setItems(restoredItems);
                setCounter(calcCounterFromItems(restoredItems));
            } else if (res.status === 404) {
                // No hay nada guardado -> dashboard vacío
                setLayouts({ lg: [], md: [], sm: [], xs: [], xxs: [] });
                setItems([]);
                setCounter(0);
            }
        } catch {
            // Error de red -> no toco nada, quedará vacío
        }
    };

    useEffect(() => {
        cargarLayouts().finally(() => setHydrated(true));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!hydrated) return;
        // Cargar datos de backend
    }, [hydrated]);

    const addItem = (type = "line") => {
        return;
        const id = String((counter || 0) + 1);
        // setCounter((c) => (c || 0) + 1);
        setItems((prev) => [
            ...prev,
            {
                i: id,
                type,
                title: `Card ${id}`,
                x: 0,
                y: Infinity,
                w: CARD_W,
                h: CARD_H,
            },
        ]);
        setLayouts((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((bp) => {
                next[bp] = [
                    ...(next[bp] || []),
                    { i: id, x: 0, y: Infinity, w: CARD_W, h: CARD_H },
                ];
            });
            return next;
        });
    }

    const removeItem = (id) => {
        setItems((prev) => prev.filter((it) => it.i !== id));
        setLayouts((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((bp) => {
                next[bp] = (next[bp] || []).filter((l) => l.i !== id);
            });
            return next;
        });
    }

    const onLayoutChange = (currLayout, allLayouts) => {
        if (!hydrated) return; // evita pisar con layout inicial
        setLayouts(
            allLayouts && Object.keys(allLayouts).length
                ? allLayouts
                : { ...layouts, [currentBreakpoint]: currLayout }
        );
        setItems((prev) =>
            prev.map((it) => {
                const found = currLayout.find((l) => l.i === it.i);
                return found
                    ? { ...it, x: found.x, y: found.y, w: found.w, h: found.h }
                    : it;
            })
        );
    }

    return (
        <div className="min-h-screen bg-neutral-50  p-4">
            <div className="mx-auto max-w-[1400px]">
                <header className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4">
                    <div>
                        <h1 className="text-2xl font-semibold tracking-tight">Dashboard</h1>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => addItem("line")}
                            className="inline-flex items-center gap-2 rounded-2xl border border-neutral-200  px-3 py-2 text-sm hover:bg-white  shadow-sm"
                        >
                            <Plus className="h-4 w-4" />
                        </button>

                        <button
                            onClick={() => {
                                scheduleSave();
                            }}
                            className="inline-flex items-center gap-2 rounded-2xl border border-neutral-200  px-3 py-2 text-sm hover:bg-white  shadow-sm"
                        >
                            <Save className="h-4 w-4" />{t("label_guardar")}
                        </button>
                    </div>
                </header>

                <div className="rounded-2xl border bg-white border-neutral-200  p-2 min-h-[130px]">
                    <ResponsiveGridLayout
                        className="layout"
                        layouts={layouts}
                        breakpoints={DEFAULT_BREAKPOINTS}
                        cols={DEFAULT_COLS}
                        rowHeight={10}
                        margin={[12, 12]}
                        containerPadding={[12, 12]}
                        isBounded={true}
                        isDraggable={true}
                        isResizable={true}
                        onLayoutChange={onLayoutChange}
                        onBreakpointChange={(bp) => {
                            setCurrentBreakpoint(bp);
                            cargarLayouts();
                            if (!hydrated) return;
                            const pick =
                                layouts[bp] && layouts[bp].length
                                    ? layouts[bp]
                                    : layouts.lg && layouts.lg.length
                                        ? layouts.lg
                                        : layouts.md && layouts.md.length
                                            ? layouts.md
                                            : layouts.sm && layouts.sm.length
                                                ? layouts.sm
                                                : layouts.xs && layouts.xs.length
                                                    ? layouts.xs
                                                    : layouts.xxs || [];
                            const byId = new Map(pick.map((l) => [l.i, l]));
                            setItems((prev) =>
                                prev.map((it) => {
                                    const pos = byId.get(it.i);
                                    return pos
                                        ? { ...it, x: pos.x, y: pos.y, w: pos.w, h: pos.h }
                                        : it;
                                })
                            );
                        }}
                        draggableHandle=".drag-handle"
                        compactType="vertical"
                    >
                        {items.length === 0 ? (
                            <div
                                key="empty"
                                className="col-span-12 p-6 text-sm text-neutral-500 min-w-[250px]"
                            >
                                {t("label_dashboard_vacio")}
                            </div>
                        ) : (
                            items.map((item) => (
                                <div
                                    key={item.i}
                                    data-grid={{
                                        i: item.i,
                                        x: item.x,
                                        y: item.y,
                                        w: item.w,
                                        h: item.h,
                                    }}
                                >
                                    <ChartCard
                                        {...item}
                                        onRemove={removeItem}
                                        datasets={{
                                            // lineData,
                                            // barData,
                                            // areaData,
                                            // pieData,
                                            // listadoCalificaciones,
                                        }}
                                    />
                                </div>
                            ))
                        )}
                    </ResponsiveGridLayout>
                </div>
            </div>
        </div>
    );
}
